/* Shared shell for the PUBLIC marketing pages only.
   - Exposes `marketingLinks` for each route's `links` export: the marketing
     stylesheet + Google Fonts (Fraunces / Schibsted Grotesk / Spline Sans Mono).
     Fonts load ONLY on these routes — the embedded admin (root.tsx) is untouched.
   - Header + Footer + the page <main> wrapper. */
import { Link } from "react-router";
import marketingHref from "../../styles/marketing.css?url";
import { SOCIAL_IMAGE } from "../../brand";
import { ArrowRight } from "./icons";

const FONT_CSS =
  "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400..650;1,9..144,400..600&family=Schibsted+Grotesk:wght@400;500;600;700&family=Spline+Sans+Mono:wght@400;500&display=swap";

/** Spread into a public route's `links` export. */
export const marketingLinks = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" as const },
  { rel: "stylesheet", href: FONT_CSS },
  { rel: "stylesheet", href: marketingHref },
];

/** One local 1200×630 social card shared by every public marketing route. */
export const socialImageMeta = () => [
  { property: "og:image", content: SOCIAL_IMAGE.url },
  { property: "og:image:width", content: SOCIAL_IMAGE.width },
  { property: "og:image:height", content: SOCIAL_IMAGE.height },
  { property: "og:image:alt", content: SOCIAL_IMAGE.alt },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:image", content: SOCIAL_IMAGE.url },
  { name: "twitter:image:alt", content: SOCIAL_IMAGE.alt },
];

const NAV = [
  { href: "/features", label: "Features" },
  { href: "/pricing", label: "Pricing" },
  { href: "/compare", label: "Compare" },
  { href: "/shopify-b2b-quote-workflow-without-plus", label: "Guide" },
];

export function MarketingHeader({ cta = "default" }: { cta?: "default" | "appstore" }) {
  return (
    <header className="qd-header">
      <div className="qd-wrap qd-header__inner">
        <Link to="/" className="qd-brand" aria-label="Quote Desk home">
          <span className="qd-brand__mark" aria-hidden>
            Q
          </span>
          Quote Desk
        </Link>
        <nav className="qd-nav" aria-label="Primary">
          <div className="qd-nav__links">
            {NAV.map((n) => (
              <Link key={n.href} to={n.href}>
                {n.label}
              </Link>
            ))}
          </div>
          <a className="qd-btn qd-btn--dark" href="https://apps.shopify.com/quote-desk">
            {cta === "appstore" ? "App Store" : "Get Quote Desk"}
            <ArrowRight />
          </a>
        </nav>
      </div>
    </header>
  );
}

const SIBLINGS = [
  { href: "https://apps.shopify.com/geo-ify", label: "GEO — AI Shopping Optimization" },
  { href: "https://apps.shopify.com/storelinter", label: "StoreLinter" },
  { href: "https://apps.shopify.com/product-quiz-2", label: "Product Quiz" },
];

export function MarketingFooter() {
  return (
    <footer className="qd-footer">
      <div className="qd-wrap">
        <div className="qd-footer__grid">
          <div className="qd-footer__brand">
            <Link to="/" className="qd-brand" aria-label="Quote Desk home">
              <span className="qd-brand__mark" aria-hidden>
                Q
              </span>
              Quote Desk
            </Link>
            <p>
              The B2B sales layer Shopify reserves for Plus merchants — quotes,
              PO PDF parsing, and AI counter-offers — on every plan. By
              Legitimate LLC.
            </p>
          </div>

          <nav aria-label="Product">
            <h4>Product</h4>
            <ul>
              <li>
                <Link to="/features">Features</Link>
              </li>
              <li>
                <Link to="/pricing">Pricing</Link>
              </li>
              <li>
                <Link to="/compare">vs Shopify Plus</Link>
              </li>
              <li>
                <a href="https://apps.shopify.com/quote-desk">Shopify App Store</a>
              </li>
            </ul>
          </nav>

          <nav aria-label="Resources">
            <h4>Resources</h4>
            <ul>
              <li>
                <Link to="/shopify-b2b-quote-workflow-without-plus">
                  B2B without Plus guide
                </Link>
              </li>
              <li>
                <Link to="/support">Support</Link>
              </li>
              <li>
                <Link to="/privacy">Privacy</Link>
              </li>
              <li>
                <Link to="/terms">Terms</Link>
              </li>
            </ul>
          </nav>

          <nav aria-label="More from Legitimate LLC">
            <h4>More apps</h4>
            <ul>
              {SIBLINGS.map((s) => (
                <li key={s.href}>
                  <a href={s.href}>{s.label}</a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="qd-footer__bar">
          <span>© {new Date().getFullYear()} Legitimate LLC. Built for Shopify B2B merchants.</span>
          <span>B2B quotes · PO PDF parsing · AI counter-offers — every plan.</span>
        </div>
      </div>
    </footer>
  );
}

/** Full-page wrapper: skip link + .qd root + header + main + footer. */
export function MarketingShell({
  children,
  headerCta,
}: {
  children: React.ReactNode;
  headerCta?: "default" | "appstore";
}) {
  return (
    <div className="qd">
      <a className="qd-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader cta={headerCta} />
      <main id="main">{children}</main>
      <MarketingFooter />
    </div>
  );
}
